import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMinus, UserPlus } from 'lucide-react';

import { Button } from '../Common/button';
import { Input } from '../Common/input';
import { profileService } from '../../services/profileService';
import organizationService from '../../services/organizationService';
import { notify } from '../../lib/notify';
import type { User } from '../../types';
import type { ProfileUser } from '../../types/profiles';

interface ProfileUsersPanelProps {
  profileId: string;
  /** Organization whose members can be added (profile org, else the admin's own org). */
  organizationId: string | null;
  users: ProfileUser[];
  loading: boolean;
  onChanged: () => Promise<void> | void;
}

const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : err?.message ?? 'Error';
};

/** Users tab of the profile editor (plan §9.2): members holding the profile, add and remove. */
export const ProfileUsersPanel: React.FC<ProfileUsersPanelProps> = ({
  profileId,
  organizationId,
  users,
  loading,
  onChanged,
}) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!organizationId) {
      setMembers([]);
      return;
    }
    organizationService
      .getOrganizationUsers(organizationId)
      .then((list) => {
        if (!cancelled) setMembers(list);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const holderIds = useMemo(() => new Set(users.map((u) => u.id)), [users]);
  const candidates = useMemo(
    () =>
      members
        .filter((m) => !holderIds.has(m.id))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [members, holderIds]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => (u.full_name ?? '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  const assign = async (userId: string, profile: string | null) => {
    setBusyUserId(userId);
    try {
      await profileService.assignToUser(userId, profile);
      notify.success(
        profile
          ? t('profiles.users.added', 'Member added to the profile')
          : t('profiles.users.removed', 'Member removed; the role default profile applies')
      );
      if (profile) setSelectedUserId('');
      await onChanged();
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <label className="text-sm font-medium mb-1 block" htmlFor="profile-add-member">
            {t('profiles.users.addMember', 'Add member')}
          </label>
          <select
            id="profile-add-member"
            className="w-full border border-input rounded-md bg-background px-3 py-2 text-sm"
            value={selectedUserId}
            disabled={!organizationId || candidates.length === 0}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <option value="">
              {organizationId
                ? t('profiles.users.pickMember', '— choose a member —')
                : t('profiles.users.noOrganization', '— no organization —')}
            </option>
            {candidates.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name ? `${m.name} (${m.email})` : m.email}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          disabled={!selectedUserId || busyUserId !== null}
          onClick={() => assign(selectedUserId, profileId)}
        >
          <UserPlus className="h-4 w-4 mr-1" />
          {t('profiles.users.assign', 'Assign')}
        </Button>
      </div>

      <div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('profiles.users.search', 'Search by name or email')}
          aria-label={t('profiles.users.search', 'Search by name or email')}
        />
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">{t('common.loading', 'Loading…')}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-muted-foreground text-sm">
          {t('profiles.users.empty', 'No member has this profile.')}
        </div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('profiles.users.columns.name', 'Name')}</th>
                <th className="px-3 py-2 font-medium">{t('profiles.users.columns.email', 'Email')}</th>
                <th className="px-3 py-2 font-medium">{t('profiles.users.columns.role', 'Role')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('profiles.columns.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-t border-border/60">
                  <td className="px-3 py-2 font-medium">{u.full_name || '—'}</td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.role}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busyUserId !== null}
                      onClick={() => assign(u.id, null)}
                      title={t('profiles.users.removeHelp', 'The member falls back to the default profile of their role.')}
                    >
                      <UserMinus className="h-4 w-4 mr-1" />
                      {t('profiles.users.remove', 'Remove')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
